import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import { getDataQualityColor, getLeakSeverityColor } from '../utils/leakDetection'
import PipelineOverlay from './PipelineOverlay'

// Fix for default markers in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

// Component to handle map events
const MapEvents = ({ onViewportChange, onZoomChange }) => {
  useMapEvents({
    moveend: (e) => {
      const map = e.target
      const bounds = map.getBounds()
      onViewportChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      })
    },
    zoomend: (e) => {
      const map = e.target
      onZoomChange(map.getZoom())
    }
  })
  return null
}

// Custom icons for different data qualities
const createCustomIcon = (color, size = 8) => {
  return L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="
      background-color: ${color};
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2]
  })
}

// Leak marker icon - more prominent and graphical
const createLeakIcon = (severity) => {
  const color = getLeakSeverityColor(severity)
  const isHighSeverity = severity > 1000
  
  return L.divIcon({
    className: 'custom-div-icon',
    html: `
      <div style="
        position: relative;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <!-- Outer pulsing ring -->
        <div style="
          position: absolute;
          width: 40px;
          height: 40px;
          border: 3px solid ${color};
          border-radius: 50%;
          opacity: 0.6;
          animation: leakPulse 2s infinite;
        "></div>
        
        <!-- Inner pulsing ring -->
        <div style="
          position: absolute;
          width: 28px;
          height: 28px;
          border: 2px solid ${color};
          border-radius: 50%;
          opacity: 0.8;
          animation: leakPulse 2s infinite 0.5s;
        "></div>
        
        <!-- Main icon -->
        <div style="
          position: relative;
          width: 24px;
          height: 24px;
          background: linear-gradient(135deg, ${color}, ${color}dd);
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 4px 12px rgba(0,0,0,0.5), 0 0 20px ${color}80;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
          color: white;
          text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
          animation: leakGlow 1.5s infinite alternate;
        ">
          ${isHighSeverity ? '⚠️' : '💨'}
        </div>
        
        <!-- Danger symbol for very high severity -->
        ${severity > 2000 ? `
          <div style="
            position: absolute;
            top: -8px;
            right: -8px;
            width: 16px;
            height: 16px;
            background: #ff0000;
            border: 2px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            color: white;
            font-weight: bold;
            animation: dangerBlink 1s infinite;
          ">!</div>
        ` : ''}
      </div>
      
      <style>
        @keyframes leakPulse {
          0% { transform: scale(0.8); opacity: 0.8; }
          50% { transform: scale(1.2); opacity: 0.4; }
          100% { transform: scale(0.8); opacity: 0.8; }
        }
        
        @keyframes leakGlow {
          0% { box-shadow: 0 4px 12px rgba(0,0,0,0.5), 0 0 20px ${color}80; }
          100% { box-shadow: 0 4px 12px rgba(0,0,0,0.5), 0 0 30px ${color}cc; }
        }
        
        @keyframes dangerBlink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0.3; }
        }
      </style>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 20]
  })
}

const MapView = ({ data, leaks, onPointSelect, onShowInTable, selectedPoint }) => {
  const mapRef = useRef()
  const [showAllPoints, setShowAllPoints] = useState(true)
  const [showLeaks, setShowLeaks] = useState(true)
  const [showPipelines, setShowPipelines] = useState(true)
  const [pipelineData, setPipelineData] = useState(null)
  const [pipelineLoading, setPipelineLoading] = useState(true)
  const [zoomLevel, setZoomLevel] = useState(15)
  // Calculate max points based on zoom level for performance
  const maxPoints = useMemo(() => {
    if (zoomLevel >= 20) return 30000  // Maximum detail - show all points
    if (zoomLevel >= 18) return 15000  // Very high detail
    if (zoomLevel >= 16) return 7500   // High detail
    if (zoomLevel >= 14) return 3000   // Medium-high detail
    if (zoomLevel >= 12) return 1500   // Medium detail
    if (zoomLevel >= 10) return 600    // Low-medium detail
    return 300 // Low detail
  }, [zoomLevel])
  const [mapType, setMapType] = useState('satellite') // 'street' or 'satellite'
  const [mapBounds, setMapBounds] = useState(null) // Current map viewport bounds

  // Load pipeline data
  useEffect(() => {
    const loadPipelineData = async () => {
      try {
        setPipelineLoading(true)
        const response = await fetch('/data/Trasy_eustream_wgs_optimized.geojson')
        if (response.ok) {
          const data = await response.json()
          setPipelineData(data)
        }
      } catch (error) {
        console.warn('Could not load pipeline data:', error)
      } finally {
        setPipelineLoading(false)
      }
    }
    
    loadPipelineData()
  }, [])

  // Memoize filtered data to avoid recalculation
  const filteredData = useMemo(() => {
    if (!data) return []
    
    let filtered = data
    
    // Apply quality filters
    if (!showAllPoints) {
      filtered = data.filter(point => 
        point.falconState === '1' && 
        point.falcon1F > 30
      )
    }
    
    // Filter by viewport bounds for performance
    if (mapBounds) {
      filtered = filtered.filter(point => {
        const inBounds = point.lat >= mapBounds.south && 
                        point.lat <= mapBounds.north && 
                        point.lng >= mapBounds.west && 
                        point.lng <= mapBounds.east
        return inBounds
      })
    }
    
    // Limit points for performance based on zoom level
    if (filtered.length > maxPoints) {
      // Smart sampling: maintain path continuity while preserving important points
      
      // 1. Always include all leak points
      const leakPoints = filtered.filter(point => 
        point.falconMeas > 500 && ['0', '1'].includes(point.falconState) && point.falcon1F > 30
      )
      
      // 2. For remaining points, use uniform sampling to maintain continuity
      const nonLeakPoints = filtered.filter(point => 
        !(point.falconMeas > 500 && ['0', '1'].includes(point.falconState) && point.falcon1F > 30)
      )
      
      const remainingSlots = maxPoints - leakPoints.length
      
      if (remainingSlots > 0 && nonLeakPoints.length > 0) {
        // Use uniform sampling to maintain path continuity
        const step = Math.max(1, Math.floor(nonLeakPoints.length / remainingSlots))
        const sampledNonLeakPoints = nonLeakPoints.filter((_, index) => index % step === 0)
        
        // If we still have too many points, take the first N
        const finalNonLeakPoints = sampledNonLeakPoints.slice(0, remainingSlots)
        
        // Combine leak points with uniformly sampled points
        filtered = [...leakPoints, ...finalNonLeakPoints]
      } else {
        // If no remaining slots, just take leak points
        filtered = leakPoints.slice(0, maxPoints)
      }
    }
    
    return filtered
  }, [data, showAllPoints, maxPoints, mapBounds])

  // Memoize bounds calculation
  const bounds = useMemo(() => {
    if (!data || data.length === 0) return null
    return L.latLngBounds(
      data.map(point => [point.lat, point.lng])
    )
  }, [data])

  // Handle zoom changes
  const handleZoomChange = useCallback((newZoom) => {
    setZoomLevel(newZoom)
  }, [])

  // Handle viewport changes
  const handleViewportChange = useCallback((newBounds) => {
    setMapBounds(newBounds)
  }, [])

  useEffect(() => {
    if (bounds && mapRef.current) {
      mapRef.current.fitBounds(bounds, { padding: [20, 20] })
    }
  }, [bounds])

  // Zoom to selected point when it changes and open popup
  useEffect(() => {
    if (selectedPoint && mapRef.current) {
      const point = [selectedPoint.lat, selectedPoint.lng]
      mapRef.current.setView(point, 20) // Zoom to maximum level (20)
      
      // Find the marker for this point and open its popup
      setTimeout(() => {
        const map = mapRef.current
        if (map) {
          // Get all markers on the map
          const markers = []
          map.eachLayer((layer) => {
            if (layer instanceof L.CircleMarker || layer instanceof L.Marker) {
              markers.push(layer)
            }
          })
          
          // Find the marker that matches our selected point
          const matchingMarker = markers.find(marker => {
            const markerPos = marker.getLatLng()
            return Math.abs(markerPos.lat - selectedPoint.lat) < 0.000001 && 
                   Math.abs(markerPos.lng - selectedPoint.lng) < 0.000001
          })
          
          // Open the popup for the matching marker
          if (matchingMarker) {
            matchingMarker.openPopup()
          }
        }
      }, 500) // Small delay to ensure map has finished zooming
    }
  }, [selectedPoint])


  if (!data) return null

  return (
    <div className="relative h-full">
      {/* Map Controls */}
      <div className="absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-lg p-4 space-y-3 min-w-[200px]">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="show-all-points"
            checked={showAllPoints}
            onChange={(e) => setShowAllPoints(e.target.checked)}
            className="rounded"
          />
          <label htmlFor="show-all-points" className="text-sm font-medium">
            Show all points ({filteredData.length.toLocaleString()}/{data.length.toLocaleString()})
          </label>
        </div>
        
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="show-leaks"
            checked={showLeaks}
            onChange={(e) => setShowLeaks(e.target.checked)}
            className="rounded"
          />
          <label htmlFor="show-leaks" className="text-sm font-medium">
            Show leaks ({leaks.length})
          </label>
        </div>
        
        <div className="border-t pt-3">
          <div className="flex items-center space-x-2 mb-2">
            <label className="text-sm font-medium text-gray-700">Map Type:</label>
            <select
              value={mapType}
              onChange={(e) => setMapType(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="street">Street View</option>
              <option value="satellite">Satellite</option>
            </select>
          </div>
        </div>
        
        <div className="border-t pt-3">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="show-pipelines"
              checked={showPipelines}
              onChange={(e) => setShowPipelines(e.target.checked)}
              disabled={pipelineLoading}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
            />
            <label htmlFor="show-pipelines" className="text-sm font-medium">
              {pipelineLoading ? 'Loading pipelines...' : 'Show pipelines'}
            </label>
            {showPipelines && !pipelineData && !pipelineLoading && (
              <div className="text-xs text-orange-600 mt-1">
                ⚠️ Pipeline data not loaded
              </div>
            )}
          </div>
        </div>
        
        {data && data.length > maxPoints && (
          <div className="border-t pt-3">
            <div className="text-xs text-blue-600 font-medium">
              🔍 Zoom in for more detail
            </div>
          </div>
        )}
        
        {/* Legend */}
        <div className="border-t pt-3">
          <p className="text-xs font-semibold text-gray-700 mb-2">Data Quality:</p>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-xs">Good</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <span className="text-xs">Medium</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-xs">Poor</span>
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <MapContainer
        ref={mapRef}
        center={[data[0]?.lat || 0, data[0]?.lng || 0]}
        zoom={15}
        className="map-container"
      >
        <MapEvents 
          onViewportChange={handleViewportChange}
          onZoomChange={handleZoomChange}
        />
        <TileLayer
          key={mapType}
          attribution={mapType === 'satellite' 
            ? '&copy; <a href="https://www.esri.com/">Esri</a> — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
            : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }
          url={mapType === 'satellite' 
            ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          }
        />
        
        {/* Pipeline Overlay */}
        <PipelineOverlay 
          key={`pipeline-${showPipelines}-${zoomLevel}`}
          pipelineData={pipelineData} 
          isVisible={showPipelines} 
        />
        
        {/* Data Points */}
        {showAllPoints && filteredData.map((point) => (
          <CircleMarker
            key={point.id}
            center={[point.lat, point.lng]}
            radius={4}
            pathOptions={{
              color: getDataQualityColor(point),
              fillColor: getDataQualityColor(point),
              fillOpacity: 0.8,
              weight: 1
            }}
            eventHandlers={{
              click: () => {
                if (onPointSelect) {
                  onPointSelect(point)
                }
              }
            }}
          >
            <Popup>
              <div className="text-sm">
                <h3 className="font-semibold mb-2">Measurement Point</h3>
                <div className="space-y-1">
                  <p><strong>FALCON_MEAS:</strong> {point.falconMeas.toFixed(1)} ppm·m</p>
                  <p><strong>FALCON_STATE:</strong> {point.falconState}</p>
                  <p><strong>FALCON_1F:</strong> {point.falcon1F.toFixed(1)}</p>
                  <p><strong>FALCON_2F:</strong> {point.falcon2F.toFixed(1)}</p>
                  <p><strong>Altitude:</strong> {point.alt.toFixed(1)} m</p>
                  <p><strong>GPS Sats:</strong> {point.gpsSatnum}</p>
                  <p><strong>Timestamp:</strong> {point.timestamp}</p>
                  {point.sourceFile && (
                    <p><strong>Source File:</strong> {point.sourceFile}</p>
                  )}
                </div>
                <div className="mt-2 pt-2 border-t">
                  <button
                    onClick={() => {
                      if (onShowInTable) {
                        onShowInTable(point)
                      }
                    }}
                    className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200"
                  >
                    📋 Show in Table
                  </button>
                </div>
              </div>
            </Popup>
          </CircleMarker>
        ))}
        
        {/* Leak Markers */}
        {showLeaks && leaks.map((leak, index) => (
          <Marker
            key={`leak-${index}`}
            position={[leak.lat, leak.lng]}
            icon={createLeakIcon(leak.falconMeas)}
            eventHandlers={{
              click: () => {
                if (onPointSelect) {
                  onPointSelect(leak)
                }
              }
            }}
          >
            <Popup>
              <div className="text-sm min-w-[280px]">
                <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-3 rounded-t-lg -m-3 mb-2">
                  <h3 className="font-bold text-lg flex items-center">
                    🚨 POTENTIAL LEAK #{index + 1}
                  </h3>
                  <div className="text-red-100 text-xs mt-1">
                    {leak.falconMeas > 2000 ? '🔴 CRITICAL SEVERITY' :
                     leak.falconMeas > 1000 ? '🟠 HIGH SEVERITY' :
                     leak.falconMeas > 500 ? '🟡 MEDIUM SEVERITY' : '🟢 LOW SEVERITY'}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="bg-gray-50 p-2 rounded border-l-4 border-red-500">
                    <div className="text-xs text-gray-600 font-semibold">FALCON_MEAS</div>
                    <div className="text-lg font-bold text-red-600">{leak.falconMeas.toFixed(1)} ppm·m</div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-blue-50 p-2 rounded">
                      <div className="text-gray-600 font-semibold">FALCON_1F</div>
                      <div className="font-bold text-blue-700">{leak.falcon1F.toFixed(1)}</div>
                    </div>
                    <div className="bg-green-50 p-2 rounded">
                      <div className="text-gray-600 font-semibold">FALCON_2F</div>
                      <div className="font-bold text-green-700">{leak.falcon2F.toFixed(1)}</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-purple-50 p-2 rounded">
                      <div className="text-gray-600 font-semibold">Altitude</div>
                      <div className="font-bold text-purple-700">{leak.alt.toFixed(1)} m</div>
                    </div>
                    <div className="bg-orange-50 p-2 rounded">
                      <div className="text-gray-600 font-semibold">Cluster Size</div>
                      <div className="font-bold text-orange-700">{leak.clusterSize} points</div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-2 rounded text-xs">
                    <div className="text-gray-600 font-semibold">Timestamp</div>
                    <div className="font-mono text-gray-800">{leak.timestamp}</div>
                  </div>
                  
                  {leak.sourceFile && (
                    <div className="bg-gray-50 p-2 rounded text-xs">
                      <div className="text-gray-600 font-semibold">Source File</div>
                      <div className="font-mono text-gray-800 truncate">{leak.sourceFile}</div>
                    </div>
                  )}
                  
                  <div className="mt-2 pt-2 border-t">
                    <button
                      onClick={() => {
                        if (onShowInTable) {
                          onShowInTable(leak)
                        }
                      }}
                      className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded hover:bg-red-200"
                    >
                      📋 Show in Table
                    </button>
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  )
}

export default MapView
